// 侧边导航菜单配置：3 级结构（组 → 分段 → 叶子），对齐 PRD C1.1 六中心
// 简化原则：
//   1) 动作类入口（新建代理商/新建商户）不进菜单 —— 总览页「新建」主按钮 + 列表页主操作位已承接
//   2) 组内按任务域分段（section），降低长列表认知负担；某段收敛后仅剩 1 叶则删 section 字段
//   3) 徽章（badge）只留给待办/审核类高频项；动态角标键见 components/Sidebar.tsx dynamicBadgeFor
// P2 菜单结构性合并（artifacts/menu-freeze.md）：9 组 65 → 41 项；被合并叶以 17 个 Tab 壳承接，
// 旧路径经 routes/legacyRedirects.tsx 301 至「壳路径?tab=x」
// 数据驱动：后续增删叶子仅改本文件，不改 Sidebar 组件
import type { ReactNode } from 'react';
import {
  LayoutDashboard,
  UserCog,
  CreditCard,
  Wallet,
  Vault,
  ShieldAlert,
  LifeBuoy,
  BarChart3,
  Settings,
} from 'lucide-react';

export interface LeafItem {
  label: string;
  path: string;
  badge?: number;
  /** 组内分段标签：连续相同值的叶子渲染为一段，段首显示分隔小标题 */
  section?: string;
}
export interface GroupItem {
  label: string;
  icon: ReactNode;
  leaves: LeafItem[];
}

export const MENU: GroupItem[] = [
  // 总览（2）
  {
    label: '总览',
    icon: <LayoutDashboard className="w-4 h-4" />,
    leaves: [
      { label: '数据概览', path: '/platform' },
      { label: '消息通知', path: '/platform/notifications', badge: 3 },
    ],
  },
  // 账户中心（8）
  {
    label: '账户中心',
    icon: <UserCog className="w-4 h-4" />,
    leaves: [
      // badge 由 useReviewBadges 动态覆盖（FIX-001/003）
      { label: '代理商资质审核', path: '/platform/account/audit/agent-onboarding', section: '审核工作台' },
      { label: '商户开户审核', path: '/platform/account/audit/merchant-onboarding', section: '审核工作台' },
      // P2 合并：资质变更审核 + 批量审核 → 变更与批量审核壳（?tab=change|batch）
      { label: '变更与批量审核', path: '/platform/account/audit/change-batch', section: '审核工作台' },
      { label: '代理商账户总览', path: '/platform/account/agents', section: '账户管理' },
      { label: '商户账户总览', path: '/platform/account/merchants', section: '账户管理' },
      { label: '上游渠道', path: '/platform/account/upstream-channels', section: '基础数据' },
      { label: '产品管理', path: '/platform/account/products', section: '基础数据' },
      // 2026-09-19-N2 S7c：费项目录自财务中心挪入产品管理分区（旧路径 /platform/finance/fee-items 重定向）
      { label: '产品费项目录', path: '/platform/account/products/fee-items', section: '基础数据' },
      // 费率中心（费率中心 Sprint）：链 A/链 B 试算 + 版本链追溯，紧邻产品费项目录
      { label: '费率中心', path: '/platform/account/rate-center', section: '基础数据' },
    ],
  },
  // 卡片中心（5）
  {
    label: '卡片中心',
    icon: <CreditCard className="w-4 h-4" />,
    leaves: [
      // P2 合并：全局卡片总览 + 卡号精准检索 → 卡片总览壳（?tab=dashboard|search）
      { label: '卡片总览', path: '/platform/card/overview' },
      { label: '卡片操作', path: '/platform/card/ops', section: '卡片操作' },
      { label: '商户卡片筛选', path: '/platform/card/by-merchant', section: '卡片操作' },
      // P2 合并：卡片交易流水 + 实时交易流 + 全局消费明细 → 交易查询壳（?tab=flow|stream|query）
      { label: '交易查询', path: '/platform/card/transactions' },
      // P2 合并：实体卡库存 + 制卡批次追踪 → 实体卡壳（?tab=inventory|batches）
      { label: '实体卡', path: '/platform/card/physical' },
    ],
  },
  // 财务中心（7）
  {
    label: '财务中心',
    icon: <Wallet className="w-4 h-4" />,
    leaves: [
      // P2 合并：资金概览 + 托管管理 → 资金总览壳（?tab=funds|escrow）
      { label: '资金总览', path: '/platform/finance/overview' },
      // P2 合并：商户对账单 + 代理商价差结算 + 资金调拨 → 结算对账壳（?tab=merchant|agent|transfer）
      { label: '结算对账', path: '/platform/finance/settlement', section: '结算对账' },
      // P2 合并：结算批次管理 + 对账管理 → 批次与对账壳（?tab=batches|recon）
      { label: '批次与对账', path: '/platform/finance/settlement-batches', section: '结算对账' },
      // 财务审核四流独立保留（P0 否决 4合1：三条 >700 行独立 OTP 审核流）
      { label: '强制退款工作台', path: '/platform/finance/force-refunds', section: '审核处理' },
      { label: '入金审核', path: '/platform/finance/deposit-reviews', section: '审核处理' },
      { label: '提现审核', path: '/platform/finance/withdrawal-reviews', section: '审核处理' },
      { label: '划转复核', path: '/platform/finance/merchant-credits', section: '审核处理' },
      { label: '充值复核', path: '/platform/finance/position-topups', section: '审核处理' },
    ],
  },
  // 账户钱包（4）（2026-09-19-N4：一级组保留，路由不变）
  {
    label: '账户钱包',
    icon: <Vault className="w-4 h-4" />,
    leaves: [
      { label: '商户钱包', path: '/platform/finance/merchant-wallet' },
      { label: 'VA 账户管理', path: '/platform/finance/va-accounts' },
      { label: '链上账户管理', path: '/platform/finance/chain-accounts' },
      { label: '多币种资金池', path: '/platform/finance/treasury-positions' },
    ],
  },
  // 风控合规（5）
  {
    label: '风控合规',
    icon: <ShieldAlert className="w-4 h-4" />,
    leaves: [
      // P2 合并：风险预警 + 团伙识别 → 风险预警壳（?tab=alerts|gangs）；badge 由 useDemoBadges 动态覆盖
      { label: '风险预警', path: '/platform/risk/alerts', badge: 3, section: '风险监控' },
      // P2 合并：风控规则 + 黑白名单 → 规则与名单壳（?tab=rules|blocklist）
      { label: '规则与名单', path: '/platform/risk/rules', section: '风险监控' },
      // P2 合并：KYC 复审 + KYC 罚款台账 + 争议处理 → KYC 复审壳（?tab=review|fines|disputes）
      { label: 'KYC 复审', path: '/platform/risk/kyc-review', section: '合规审核' },
      // 合规管理：1311 行 3-Tab 独立页，不合并（P0 裁决）
      { label: '合规管理', path: '/platform/risk/compliance', section: '合规审核' },
      // P2 合并：AML 合规检查 + 3DS 中转管理 → AML 与 3DS 壳（?tab=aml|3ds）
      { label: 'AML 与 3DS', path: '/platform/risk/aml', section: '合规审核' },
    ],
  },
  // 运营中心（4）
  {
    label: '运营中心',
    icon: <LifeBuoy className="w-4 h-4" />,
    leaves: [
      // P2 合并：内容管理 + 活动配置 + 运营驾驶舱 → 内容与活动壳（?tab=content|campaigns|cockpit）
      { label: '内容与活动', path: '/platform/ops/content' },
      // P2 合并：客服知识库 + 客服工单 + 上游邮件拦截 → 客户服务壳（?tab=kb|tickets|email）；
      // 工单角标挂壳基路径（Sidebar dynamicBadgeFor → ticketOpenCount），静态值兜底
      { label: '客户服务', path: '/platform/ops/service', badge: 2 },
      // AI 供应商管理：684 行独立页，不合并
      { label: 'AI 供应商管理', path: '/platform/ops/ai-providers', section: '运维配置' },
      // P2 合并：配置中心 + 日志异常分析 + 监控大盘与告警 → 运维监控壳（?tab=config|logs|alerts）
      { label: '运维监控', path: '/platform/ops/sre', section: '运维配置' },
    ],
  },
  // 报表中心（3）
  {
    label: '报表中心',
    icon: <BarChart3 className="w-4 h-4" />,
    leaves: [
      // P2 合并：BI 数据看板 + 预置报表模板 → 报表分析壳（?tab=bi|templates）
      { label: '报表分析', path: '/platform/report/dashboard' },
      // P2 合并：报表订阅推送 + 报表导出 → 订阅与导出壳（?tab=subscriptions|export）
      { label: '订阅与导出', path: '/platform/report/subscriptions' },
      // P2 合并：报表权限脱敏 + 报表评论协同 + 报表异常标注 → 协同治理壳（?tab=acl|collab|anomaly）
      { label: '协同治理', path: '/platform/report/collab' },
    ],
  },
  // 系统（3）
  {
    label: '系统',
    icon: <Settings className="w-4 h-4" />,
    leaves: [
      { label: '出站邮件服务', path: '/platform/system/email-smtp' },
      { label: '安全配置', path: '/platform/system/security' },
      { label: '基础设置', path: '/platform/system/settings' },
    ],
  },
];
